import React, { useState } from 'react';
import styled from 'styled-components';
import {
  BoltIcon,
  CircleStackIcon,
  BeakerIcon,
  SignalSlashIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { SIGNALK_SOURCE, SIGNALK_SOURCE_LABELS } from '../../../utils/signalkWidgetData';

/** In-flow badge — never absolute-positioned over titles. */
const IndicatorWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  z-index: 2;
  cursor: help;
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  padding: 3px 7px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(4px);
  transition: background 0.2s ease-in-out;
  pointer-events: auto;
  margin-left: 8px;

  &:hover {
    background: rgba(30, 41, 59, 0.95);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #1f2937;
  color: white;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 11px;
  z-index: 1001;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: none;
  max-width: 260px;
  white-space: normal;
  text-align: left;
  line-height: 1.35;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 10px;
    border: 4px solid transparent;
    border-bottom-color: #1f2937;
  }
`;

const DataSourceIndicator = ({ isLive, isDemo = false, lastUpdate, sourceType }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'unknown time';

    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffSeconds = Math.floor((now - date) / 1000);

      if (diffSeconds < 60) {
        return `${diffSeconds}s ago`;
      }
      if (diffSeconds < 3600) {
        return `${Math.floor(diffSeconds / 60)}m ago`;
      }
      if (diffSeconds < 86400) {
        return `${Math.floor(diffSeconds / 3600)}h ago`;
      }
      return date.toLocaleDateString();
    } catch {
      return 'unknown time';
    }
  };

  const formatFullDate = (timestamp) => {
    if (!timestamp) return 'unknown date';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'unknown date';
    }
  };

  const resolvedType = sourceType
    ? sourceType
    : isDemo
      ? SIGNALK_SOURCE.DEMO
      : isLive
        ? SIGNALK_SOURCE.LIVE
        : SIGNALK_SOURCE.DATABASE;

  // Don't paint a scary red slash on every tile when we still have a reading.
  if (resolvedType === SIGNALK_SOURCE.NONE) {
    return null;
  }

  const tooltipText = (() => {
    switch (resolvedType) {
      case SIGNALK_SOURCE.LIVE:
        return `Live — updated ${formatTimestamp(lastUpdate)}`;
      case SIGNALK_SOURCE.STALE:
        return `Path not updating recently (last: ${formatFullDate(lastUpdate)})`;
      case SIGNALK_SOURCE.OFFLINE:
        return lastUpdate
          ? `Boat not streaming — last reading ${formatFullDate(lastUpdate)}`
          : 'Boat not streaming — no recent data';
      case SIGNALK_SOURCE.DEMO:
        return 'Demo data — sample values for preview';
      case SIGNALK_SOURCE.DATABASE:
        return lastUpdate
          ? `Last saved reading ${formatFullDate(lastUpdate)}`
          : 'Showing last saved reading from database';
      default:
        return `Last update ${formatFullDate(lastUpdate)}`;
    }
  })();

  const ariaLabel = `${SIGNALK_SOURCE_LABELS[resolvedType] || resolvedType} — ${tooltipText}`;

  const iconColor = (() => {
    switch (resolvedType) {
      case SIGNALK_SOURCE.LIVE:
        return '#10b981';
      case SIGNALK_SOURCE.STALE:
        return '#f59e0b';
      case SIGNALK_SOURCE.OFFLINE:
        return '#ef4444';
      case SIGNALK_SOURCE.DEMO:
        return '#8b5cf6';
      case SIGNALK_SOURCE.DATABASE:
        return '#38bdf8';
      default:
        return '#9ca3af';
    }
  })();

  return (
    <IndicatorWrapper
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      <IconWrapper>
        {resolvedType === SIGNALK_SOURCE.LIVE && (
          <BoltIcon className="w-3.5 h-3.5" style={{ color: iconColor }} aria-hidden="true" />
        )}
        {resolvedType === SIGNALK_SOURCE.STALE && (
          <ClockIcon className="w-3.5 h-3.5" style={{ color: iconColor }} aria-hidden="true" />
        )}
        {resolvedType === SIGNALK_SOURCE.DATABASE && (
          <CircleStackIcon className="w-3.5 h-3.5" style={{ color: iconColor }} aria-hidden="true" />
        )}
        {resolvedType === SIGNALK_SOURCE.DEMO && (
          <BeakerIcon className="w-3.5 h-3.5" style={{ color: iconColor }} aria-hidden="true" />
        )}
        {resolvedType === SIGNALK_SOURCE.OFFLINE && (
          <SignalSlashIcon className="w-3.5 h-3.5" style={{ color: iconColor }} aria-hidden="true" />
        )}
        {!Object.values(SIGNALK_SOURCE).includes(resolvedType) && (
          <SignalSlashIcon className="w-3.5 h-3.5" style={{ color: iconColor }} aria-hidden="true" />
        )}
      </IconWrapper>
      <Tooltip visible={showTooltip}>{tooltipText}</Tooltip>
    </IndicatorWrapper>
  );
};

export default DataSourceIndicator;
